import { z } from "zod";

/**
 * URL search params for `/$workspaceSlug/recruitment`, the jobs list
 * (HRMS-REC-UX P4.3).
 *
 * Two keys, both filters on the one list this page draws. They are in the URL
 * rather than in `useState` (S3) for the reason every filter in this feature
 * is: "the open engineering roles" is a view a recruiter sends to a hiring
 * manager, and a refresh, the back button and a shared link should all land on
 * the rows somebody was actually looking at.
 *
 * **The names are the five-tab page's names, kept on purpose.** This route
 * held a strip whose openings tab wrote `openingQ` and `openingStatus`, and
 * the path has deliberately not moved, so a link somebody already shared
 * (`?tab=openings&openingQ=engineer`) still lands on the same filtered rows.
 * Renaming them to `jobQ`/`jobStatus` would break that for a word, which is
 * the same trade `/recruitment` itself refused when `/hiring` was considered.
 * They stay per-key prefixed rather than becoming a bare `q`/`status` pair for
 * the reason the strip's file gives: a prefix costs nothing and it is what
 * stops two lists on one route from reading each other's filter.
 *
 * **Not `.strict()`, also on purpose.** Half the links to this page were saved
 * while it was five tabs, so they carry `tab`, `opening`, `candStage`, `reqQ`,
 * `reqStatus` or `planQ`. Zod's default is to STRIP an unknown key, which is
 * exactly right here: the bookmark opens the jobs list and quietly forgets the
 * dead param, where a strict schema would answer a rearranged product with a
 * router error. The cost is that a typo (`openingQry=x`) is silently dropped
 * rather than shouted about, which is the cheaper of the two failures.
 */
export const recruitmentHomeSearchSchema = z.object({
  /// The shore's own `q` over the job title, debounced into the URL.
  openingQ: z.string().optional(),
  /// The OPENING's lifecycle, not an applicant status. A draft is `open` and
  /// unpublished, so there is no third value to filter by here; the list says
  /// which of the two an open job is on the row itself.
  openingStatus: z.enum(["open", "closed"]).optional(),
  /// The dead five-tab key, kept ONLY so a saved link can be forwarded rather
  /// than silently flattened (P4.9).
  ///
  /// Stripping it, which is what every other legacy key gets, is right for a
  /// filter: the reader wanted these rows and still gets them. It is wrong for
  /// `tab`, because `?tab=requisitions` was a link to a DIFFERENT SURFACE, and
  /// landing that reader on the jobs list answers a question they did not ask.
  /// Parsed here, acted on by the route, and never written back.
  tab: z.enum(["candidates", "openings", "requisitions", "staffing-plans", "letters"]).optional(),
});

export type RecruitmentHomeSearch = z.infer<typeof recruitmentHomeSearchSchema>;

/// Where a saved five-tab link should end up now, or null when this page is
/// already the right answer. Candidates has no destination on purpose: its
/// board was cross-opening and is now per job, so there is no single job to
/// forward to and the jobs list is the honest landing place.
export function legacyTabDestination(
  tab: RecruitmentHomeSearch["tab"],
): "requisitions" | "staffingPlans" | "letters" | null {
  if (tab === "requisitions") return "requisitions";
  if (tab === "staffing-plans") return "staffingPlans";
  if (tab === "letters") return "letters";
  return null;
}

/// The slice of the URL the jobs list's toolbar reads and writes.
export interface HiringJobFilterValues {
  /// The search box's live text. The URL holds the debounced copy.
  query: string;
  status: "open" | "closed" | undefined;
}

/**
 * Whether anything is narrowing the list.
 *
 * This is the whole of Q5's question: a workspace with no jobs at all gets the
 * full empty state, and a reader who filtered themselves down to nothing gets
 * the toolbar back with a message inside the table body. Getting the two
 * confused hides the filter that caused the emptiness.
 *
 * A search of only whitespace is not a filter: it narrows nothing on the shore
 * and it would otherwise turn "no jobs yet" into "nothing matches" after a
 * stray space.
 */
export function hasJobFilter(search: RecruitmentHomeSearch): boolean {
  return (search.openingQ ?? "").trim() !== "" || search.openingStatus !== undefined;
}
